import Api, { ApiError } from '../api/Api';
import GdlApi from '../api/GdlApi';
import { GDL_INIT, GDL_QUIT, GDL_RESET } from '../constants/app';
import { AUTH_LOGIN_SUCCESS, AUTH_LOGIN_FAILURE } from '../constants/auth';
import { API_ACTION, GDL_ACTION, initGdlAction } from '../actions/api';
import { setAuthenticated } from '../actions/auth';
import { setGraphicsStatus, updateWorkAreas } from '../actions/engineering';
import { addUnityCommand } from '../actions/unity';
import forIn from 'lodash/forIn';
import { STATUS_OFF, STATUS_ON } from '../constants/common';
import { toast, modal } from '../actions/notification';
import * as pages from '../constants/pages';

/**
 * This method returns a function that accepts two parameters: an action type and a promise.
 *
 * Depending on the action type, in most cases this returned function will in turn return
 * the result of the promise called with the appropriate api (Api or GdlApi).  There are
 * two special cases:
 *   1) If the GDL session has not been started, we dispatch the initGdlAction
 *   2) If the action type is GDL_INIT, we don't check to see if the GDL action has been started since
 *      we're taking that action now
 *
 * Calls to Api simply return the api's promise (usually an async fetch() call)
 * Calls to GdlApi (except for GDL_INIT) get a then() which dispatches actions based on the received update
 *
 * @param dispatch
 * @returns {function(*, *)}
 */
function actionPromise(dispatch) {
  const apiAuthToken = window.sessionStorage.getItem('token');
  const api = new Api({ token: apiAuthToken });
  const gdl = new GdlApi();

  return async (a, p) => {
    if (a.type === API_ACTION) {
      // student endpoint apis simply pass through with the api call's promise
      return p(api);
    } else if (a.type === GDL_ACTION) {
      // if we don't have a session yet, start one and resume after it's done
      if (!gdl.initialized) {
        await dispatch(initGdlAction());
      }

      // call the promise and check for any updates to apply
      return p(gdl).then((result) => {
        // dispatch actions based on what's inside the result
        if (result.nodes) {
          dispatch(updateWorkAreas(result.nodes));
        }
        if (result.graphics) {
          forIn(result.graphics, (data, node) => {
            if (data.hasOwnProperty('status')) {
              dispatch(
                setGraphicsStatus(
                  node,
                  data.status === 'on' ? STATUS_ON : STATUS_OFF
                )
              );
            }
            if (data.hasOwnProperty('unity') && data.unity) {
              data.unity.forEach((command) => {
                dispatch(addUnityCommand(command));
                console.debug('dispatching', command);
              });
            }
          });
        }

        return result;
      });
    } else if (a.type === GDL_INIT || a.type === GDL_QUIT) {
      return p(gdl);
    }
    if (a.type === AUTH_LOGIN_SUCCESS) {
      api.setConfig({ token: a.result.token });
    } else if (a.type === AUTH_LOGIN_FAILURE) {
      api.setConfig({ token: '' });
    } else {
      return null;
    }
  };
}

/**
 * The actual middleware.  Calls actionPromise() to construct the Apis, then uses
 * this function generator (NOT an ES6 generator) to get the action promise.
 *
 * @param dispatch
 * @param getState
 */
const apiMiddleware = ({ dispatch, getState }) => (next) => {
  const apg = actionPromise(dispatch);

  return (action) => {
    const { promise, types, ...rest } = action;

    if (!promise) {
      return next(action);
    }

    const [REQUEST, SUCCESS, FAILURE] = types;

    next({ ...rest, type: REQUEST });

    const ap = apg(action, promise);
    ap.then((result) => {
      next({ ...rest, result, type: SUCCESS });
    }).catch((error) => {
      // dispatch a modal (or toast notification) but only if page is not page_home
      let page = getState().app.page;
      if (page != pages.PAGE_HOME) {
        if (
          error.message === 'ERR_FAILED_SESSION' ||
          error.responseCode === 401
        ) {
          dispatch(
            modal(
              'Error',
              'Your session has expired. Please log out and log in again.',
              undefined,
              { backdrop: true, logoutOnClose: true }
            )
          );
        } else {
          dispatch(
            modal('Error', error.message, undefined, {
              backdrop: true,
            })
          );
        }
      }

      next({ ...rest, error: error, type: FAILURE });
    });

    return ap;
  };
};

export default apiMiddleware;
